/**
 * AI CONTEXT
 * Purpose: composition context for front-desk/admin screens.
 * Combines: useAdminViewData + useExceptionListData + selected session IDs + selected date.
 * Exposes: selected/all sessions, student tasks, exception maps, readiness.
 * Used by: SuperView, AdminView, TeacherViewContextProvider.
 * Refactor: split session selection state from data aggregation responsibilities.
 */
import dayjs, { Dayjs } from "dayjs"
import { isEqual, noop } from "lodash"
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { StudentTaskCardData } from "#customTypes/composedTypes/studentTaskTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"
import useAdminViewData, {
  AdminViewClassSession,
} from "#hooks/newHooks/useAdminViewData"
import useExceptionListData, {
  ExceptionDataBySessionId,
  StudentsByExceptionType,
} from "#hooks/newHooks/useExceptionListData"
import { AttendanceSummary } from "#utils/classHelpers"
import {
  PresentHighlightsByStudentId,
  pruneCompletedHighlights,
  pruneCompletedTaskLineToggles,
  selectActiveHighlights,
} from "#utils/taskHelpers"

interface AdminViewContextType {
  isReady: boolean

  // Sessions
  allSessions: AdminViewClassSession[]
  selectedSessions: AdminViewClassSession[]
  setSelectedSessionIds: (sessionIds: Uuid[]) => void

  // Classes
  classesFromDb: ClassConstructor[] | undefined

  // Date
  selectedDate: Dayjs
  setSelectedDate: (date: Dayjs) => void

  // Students
  sessionStudents: StudentConstructor[]
  studentTasks: StudentTaskCardData[]

  // Present-triggered task highlights (JARVIS-69). Only students whose task
  // card is still open are kept; attendance rows call the setters.
  presentHighlights: PresentHighlightsByStudentId
  markStudentPresent: (studentId: Uuid) => void
  clearPresentHighlight: (studentId: Uuid) => void

  // Local per-machine flag toggles for roster task lines (JARVIS-69 follow-up).
  // With no toggle recorded, the line is open exactly while the student is
  // present (derived from synced data). A manual toggle wins either way — open
  // early to peek, or close while present — until the student's status changes
  // (setTaskLineOpen(id, null) resets to automatic).
  taskLineToggles: Record<Uuid, boolean>
  setTaskLineOpen: (studentId: Uuid, open: boolean | null) => void

  // Exception List
  studentsByExceptionType: StudentsByExceptionType
  teachersById: Record<Uuid, UserConstructor>
  studentsById: Record<Uuid, StudentConstructor>
  exceptionDataBySessionId: ExceptionDataBySessionId
  attendanceSummariesBySessionId: Record<
    Uuid,
    {
      summary: AttendanceSummary
      sessionId: Uuid
    }
  >
}

export const AdminViewContext = createContext<AdminViewContextType>({
  isReady: false,

  // Sessions
  allSessions: [],
  selectedSessions: [],
  setSelectedSessionIds: noop,

  // Classes
  classesFromDb: [],

  // Date
  selectedDate: dayjs(),
  setSelectedDate: noop,

  // Students
  sessionStudents: [],
  studentTasks: [],
  presentHighlights: {},
  markStudentPresent: noop,
  clearPresentHighlight: noop,
  taskLineToggles: {},
  setTaskLineOpen: noop,

  // Exception List
  studentsByExceptionType: {},
  teachersById: {},
  studentsById: {},
  attendanceSummariesBySessionId: {},
  exceptionDataBySessionId: {},
})

const AdminViewContextProvider = ({ children }: { children: ReactNode }) => {
  const [selectedSessionIds, setSelectedSessionIds] = useState<Uuid[]>([])

  const handleSetSelectedSessionIds = (sessionIds: Uuid[]) => {
    const sortedSessionIds = sessionIds?.slice().sort() || []

    if (isEqual(selectedSessionIds, sortedSessionIds)) {
      return
    } else {
      setSelectedSessionIds(sortedSessionIds)
    }
  }

  const {
    processedSessions,
    studentTasks,
    // TODO: Keep an eye on this default. It may cause infinite rerenders
    sessionStudents = [],
    classesFromDb,
    selectedDate,
    setSelectedDate,
    isReady: isAdminViewReady,
  } = useAdminViewData()

  const selectedSessions = useMemo(() => {
    return processedSessions.filter((session) =>
      selectedSessionIds.includes(session.id)
    )
  }, [processedSessions, selectedSessionIds])

  const {
    studentsByExceptionType,
    teachersById,
    studentsById,
    attendanceSummariesBySessionId,
    exceptionDataBySessionId,
    isReady: isExceptionListReady,
  } = useExceptionListData(selectedSessions)

  // TODO: We seem to rerender like 5 times on attendance change
  const isReady = isAdminViewReady && isExceptionListReady

  const [rawPresentHighlights, setRawPresentHighlights] =
    useState<PresentHighlightsByStudentId>({})
  const [taskLineToggles, setTaskLineToggles] = useState<Record<Uuid, boolean>>(
    {}
  )

  // Real clock, not getNow(): this drives an elapsed-time animation, and the
  // pinned MOCK_NOW would freeze it in the test stack.
  const markStudentPresent = useCallback((studentId: Uuid) => {
    setRawPresentHighlights((prev) => ({ ...prev, [studentId]: Date.now() }))
  }, [])

  const clearPresentHighlight = useCallback((studentId: Uuid) => {
    setRawPresentHighlights((prev) => {
      if (!(studentId in prev)) return prev
      const next = { ...prev }
      delete next[studentId]
      return next
    })
  }, [])

  // `null` forgets the manual toggle so the line returns to its automatic
  // state (open while present); true/false pin it open/closed.
  const setTaskLineOpen = useCallback(
    (studentId: Uuid, open: boolean | null) => {
      setTaskLineToggles((prev) => {
        if (open === null) {
          if (!(studentId in prev)) return prev
          const next = { ...prev }
          delete next[studentId]
          return next
        }

        if (prev[studentId] === open) return prev
        return { ...prev, [studentId]: open }
      })
    },
    []
  )

  // A highlight only glows while the student's card still has open tasks.
  const presentHighlights = useMemo(
    () => selectActiveHighlights(rawPresentHighlights, studentTasks),
    [rawPresentHighlights, studentTasks]
  )

  // Hiding is not enough — a retained timestamp would be reapplied the moment a
  // new task arrived for a student still marked present. Prune only on a
  // positively observed completed card, never on absence from the list: an
  // in-flight query starts empty, and treating that as "all completed" would
  // wipe real arrivals on every date change.
  useEffect(() => {
    setRawPresentHighlights((prev) =>
      pruneCompletedHighlights(prev, studentTasks)
    )
    setTaskLineToggles((prev) =>
      pruneCompletedTaskLineToggles(prev, studentTasks)
    )
  }, [studentTasks])

  // Arrivals are inherently about the day on screen, so switching days drops
  // them. This is also what keeps a highlight from surviving into another date's
  // roster, which pruning-on-absence used to (wrongly) cover.
  useEffect(() => {
    setRawPresentHighlights({})
  }, [selectedDate])

  const value = {
    isReady,

    // Sessions
    allSessions: processedSessions,
    selectedSessions,
    setSelectedSessionIds: handleSetSelectedSessionIds,
    classesFromDb,

    // Date
    selectedDate,
    setSelectedDate,

    // Student Tasks
    sessionStudents,
    studentTasks,
    presentHighlights,
    markStudentPresent,
    clearPresentHighlight,
    taskLineToggles,
    setTaskLineOpen,

    // Exception List
    studentsByExceptionType,
    teachersById,
    studentsById,
    attendanceSummariesBySessionId,
    exceptionDataBySessionId,
  }

  return (
    <AdminViewContext.Provider value={value}>
      {children}
    </AdminViewContext.Provider>
  )
}

export const useAdminViewContext = () => {
  return useContext(AdminViewContext)
}

export default AdminViewContextProvider
